import React, { useCallback, useState, useMemo } from 'react';
import Select from 'react-select';
import { debounce } from 'lodash';
import { UICollegeName } from 'types';
import { useGetCollegeName } from 'src/hooks/collegeName.hooks';
import { useMutation } from 'react-query';
import { createCollegeName } from 'src/apis/collegeName';
import { useToast } from 'src/context/ToastProvider';

interface CollegeSelectProps {
	disabled?: boolean;
	isClearable?: boolean;
	isMulti?: boolean; // Add this prop
	value?: { value: string; label: string } | { value: string; label: string }[];
	onChange?: (option: { value: string; label: string } | { value: string; label: string }[]) => void;
	styleType?: 'flowbite-select' | 'my-select-plans';
	allowedColleges?: string[];
	className?: string;
	placeholder?: string;
}

const namePossilitiesCache: {
	[key: string]: { keywords: { [key: string]: boolean }; mkl: number };
} = {};

const isCap = (char: string) => {
	const code = char.charCodeAt(0);
	return code >= 65 && code <= 90;
};

const createCacheForName = (name: string) => {
	const keywords: { [key: string]: boolean } = {};
	const initialChars = name
		.split(' ')
		.filter(word => word[0] && isCap(word[0]))
		.map(word => word[0].toLowerCase());
	for (let i = 1; i <= initialChars.length; i++) {
		keywords[initialChars.slice(0, i).join('')] = true;
	}
	namePossilitiesCache[name] = {
		keywords,
		mkl: initialChars.length,
	};
};

const isSubSequence = (name: string, inputValue: string) => {
	const inputValueLength = inputValue.length;
	let matchedLength = 0;
	for (let i = 0; i < name.length && matchedLength < inputValueLength; i++) {
		if (name[i].toLowerCase() === inputValue[matchedLength].toLowerCase()) {
			matchedLength += 1;
		}
	}
	return matchedLength === inputValueLength;
};

const calculateMatch = (item: UICollegeName, inputValue: string) => {
	let match = 0;
	const name = item.name;
	const lName = name.toLowerCase();
	const firstKeyword = inputValue.split(' ')[0];
	const fullKeyword = inputValue.replace(/\s/g, '');

	if (!inputValue || lName === 'other') {
		match = 0.10001;
	}

	if (!namePossilitiesCache[name]) {
		createCacheForName(name);
	}

	if (namePossilitiesCache[name].keywords[fullKeyword]) {
		match += 0.8 * (inputValue.length / namePossilitiesCache[name].mkl);
	} else if (firstKeyword && namePossilitiesCache[name].keywords[firstKeyword]) {
		match += 0.3 * (inputValue.length / namePossilitiesCache[name].mkl);
	}

	if (lName.includes(inputValue)) {
		const foundAt: number = lName.indexOf(inputValue);
		match += 0.2;
		match += 0.06 * (1 - (foundAt + 1) / lName.length);
		match += 0.04 * (foundAt === 0 || lName[foundAt - 1] === ' ' ? 1 : 0);
	}

	if (isSubSequence(lName, inputValue)) {
		match += 0.15 + 0.05 * (inputValue.length / lName.length);
	}

	return { item, match };
};

const CollegeSelect: React.FC<CollegeSelectProps> = ({
	value,
	onChange,
	className,
	disabled = false,
	isClearable = true,
	isMulti = false, // Default to single select
	allowedColleges,
	placeholder = 'Select College',
	styleType = 'flowbite-select',
}) => {
	const limit = 10;
	const toast = useToast();
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState('');
	const [collegeNames, setCollegeNames] = useState<UICollegeName[]>([]);
	const [hasMore, setHasMore] = useState(true);
	const [createdCollegeNames, setCreatedCollegeNames] = useState<UICollegeName[]>([]);

	const { isLoading } = useGetCollegeName({
		page,
		limit,
		search,
		allowedColleges: allowedColleges?.join(','),
		onComplete: data => {
			setCollegeNames(prev => {
				const newColleges = [...prev, ...data.collegeNames];
				const uniqueColleges = Array.from(new Set(newColleges.map(c => c.name))).map(
					name => newColleges.find(c => c.name === name)!,
				);
				return uniqueColleges;
			});
			setHasMore(data.collegeNames.length > 0);
		},
	});

	const filteredCollegeOptions = useMemo(() => {
		const allColleges = [...collegeNames, ...createdCollegeNames];
		return allColleges
			.map(item => calculateMatch(item, search))
			.filter(i => i.match > 0.1)
			.sort((a, b) => b.match - a.match)
			.map(i => ({ value: i.item._id, label: i.item.name }));
	}, [collegeNames, createdCollegeNames, search]);

	const loadMoreOptions = useCallback(() => {
		if (!isLoading && hasMore) {
			setPage(prevPage => prevPage + 1);
		}
	}, [isLoading, hasMore]);

	const debouncedLoadMoreOptions = useMemo(() => debounce(loadMoreOptions, 300), [loadMoreOptions]);

	const debouncedSetSearch = useCallback(
		debounce((value: string) => {
			setSearch(value);
			setPage(1);
			setHasMore(true);
		}, 300),
		[],
	);

	const createCollegeNameFn = useMutation({
		mutationFn: ({ name }: { name: string }) => createCollegeName({ name }),
		onSuccess: ({ collegeName }) => {
			setCreatedCollegeNames(prev => [...prev, collegeName]);
			onChange &&
				onChange(
					isMulti
						? [...(value as { value: string; label: string }[]), { value: collegeName._id, label: collegeName.name }]
						: { value: collegeName._id, label: collegeName.name },
				);
			toast({
				title: 'College Name Created',
				type: 'success',
			});
		},
		onError: error => {
			console.log(error);
			toast({
				title: 'Error while creating college name',
				type: 'error',
			});
		},
	});

	return (
		<Select
			required
			id="collegeName"
			menuPosition="fixed"
			isDisabled={disabled}
			isLoading={isLoading}
			className={className}
			isClearable={isClearable}
			isMulti={isMulti} // Pass the isMulti prop
			placeholder={placeholder}
			classNamePrefix={styleType}
			options={filteredCollegeOptions}
			value={value ? value : undefined}
			onInputChange={inputValue => {
				debouncedSetSearch(inputValue);
			}}
			onMenuScrollToBottom={debouncedLoadMoreOptions}
			loadingMessage={() => 'Loading more options...'}
			onChange={option =>
				onChange && onChange(option as { value: string; label: string } | { value: string; label: string }[])
			}
			noOptionsMessage={({ inputValue }) => (isLoading ? 'Loading...' : inputValue ? 'No options' : 'Type to search')}
			filterOption={() => true}
		/>
	);
};

export default CollegeSelect;
